import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import swal from "sweetalert";

const emptyAccount = {
  bankAccountID: 0,
  bankName: "",
  accountTitle: "",
  accountNumber: "",
  iban: "",
  swiftCode: "",
  branchName: "",
  instructions: "",
  currency: "",
  sortOrder: 0,
  isActive: true,
};

const ManageBankAccounts = () => {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyAccount);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_BILLING_BANK_ACCOUNTS);
      if (response.data.status) {
        setAccounts(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setAccounts([]);
        showErrorToast(response.data.message || "Failed to load bank accounts");
      }
    } catch (err) {
      showErrorToast("Failed to load bank accounts");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const openAdd = () => {
    setForm({ ...emptyAccount });
    setShowModal(true);
  };

  const openEdit = (account) => {
    setForm({
      bankAccountID: account.bankAccountID,
      bankName: account.bankName || "",
      accountTitle: account.accountTitle || "",
      accountNumber: account.accountNumber || "",
      iban: account.iban || "",
      swiftCode: account.swiftCode || "",
      branchName: account.branchName || "",
      instructions: account.instructions || "",
      currency: account.currency || "",
      sortOrder: account.sortOrder ?? 0,
      isActive: account.isActive !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.bankName?.trim() || !form.accountNumber?.trim()) {
      showErrorToast("Bank name and account number are required");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        bankAccountID: form.bankAccountID,
        bankName: form.bankName,
        accountTitle: form.accountTitle,
        accountNumber: form.accountNumber,
        iban: form.iban,
        swiftCode: form.swiftCode,
        branchName: form.branchName,
        instructions: form.instructions,
        currency: form.currency,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
        isActive: form.isActive,
      };

      if (form.bankAccountID > 0) {
        await axiosInstance.put(APIs.PUT_UPDATE_BILLING_BANK_ACCOUNT, payload);
        showSuccessToast("Bank account updated");
      } else {
        await axiosInstance.post(APIs.POST_ADD_BILLING_BANK_ACCOUNT, payload);
        showSuccessToast("Bank account added");
      }

      setShowModal(false);
      fetchAccounts();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save bank account");
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (bankAccountID) => {
    const confirmed = await swal({
      title: "Deactivate bank account?",
      text: "This account will no longer appear on restaurant payment screens.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      await axiosInstance.patch(APIs.PATCH_DEACTIVATE_BILLING_BANK_ACCOUNT, null, {
        params: { bankAccountID },
      });
      showSuccessToast("Bank account deactivated");
      fetchAccounts();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to deactivate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Preloader />}

      <div className="container-fluid px-4 py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Billing Bank Accounts</h4>
            <p className="text-muted mb-0">
              Global bank details shown to restaurants when paying by bank transfer.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            <i className="fa fa-plus me-2"></i>
            Add Bank Account
          </button>
        </div>

        <div className="card">
          <div className="card-body">
            {accounts.length === 0 ? (
              <div className="alert alert-info mb-0">No bank accounts configured yet.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="bg-light">
                    <tr>
                      <th>Bank</th>
                      <th>Account title</th>
                      <th>Account #</th>
                      <th>IBAN</th>
                      <th>Currency</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.bankAccountID}>
                        <td>{account.bankName}</td>
                        <td>{account.accountTitle}</td>
                        <td>{account.accountNumber}</td>
                        <td>{account.iban || "—"}</td>
                        <td>{account.currency || "—"}</td>
                        <td>{account.sortOrder}</td>
                        <td>
                          <span className={`badge bg-${account.isActive ? "success" : "secondary"}`}>
                            {account.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEdit(account)}
                            title="Edit"
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          {account.isActive && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeactivate(account.bankAccountID)}
                              title="Deactivate"
                            >
                              <i className="fa fa-ban"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={form.bankAccountID > 0 ? "Edit Bank Account" : "Add Bank Account"}
        onSubmit={handleSubmit}
        submitButtonText="Save"
        showButton={true}
        modalSize="modal-lg"
        isCentered
      >
        <div className="row g-3">
          <div className="col-md-6">
            <FormInput
              label="Bank name"
              type="text"
              value={form.bankName}
              onchange={(e) => setForm({ ...form, bankName: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Account title"
              type="text"
              value={form.accountTitle}
              onchange={(e) => setForm({ ...form, accountTitle: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Account number"
              type="text"
              value={form.accountNumber}
              onchange={(e) => setForm({ ...form, accountNumber: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="IBAN"
              type="text"
              value={form.iban}
              onchange={(e) => setForm({ ...form, iban: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="SWIFT / BIC"
              type="text"
              value={form.swiftCode}
              onchange={(e) => setForm({ ...form, swiftCode: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Branch"
              type="text"
              value={form.branchName}
              onchange={(e) => setForm({ ...form, branchName: e.target.value })}
            />
          </div>
          <div className="col-md-4">
            <FormInput
              label="Currency"
              type="text"
              value={form.currency}
              onchange={(e) => setForm({ ...form, currency: e.target.value })}
            />
          </div>
          <div className="col-md-4">
            <FormInput
              label="Sort order"
              type="number"
              value={form.sortOrder}
              onchange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            />
          </div>
          {form.bankAccountID > 0 && (
            <div className="col-md-4 d-flex align-items-end">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active
                </label>
              </div>
            </div>
          )}
          <div className="col-12">
            <label className="form-label">Payment instructions</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              placeholder="e.g. Use invoice number as payment reference"
            />
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default ManageBankAccounts;
